import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation, type Language } from '../contexts/TranslationContext';
import { guestQuestionService, GUEST_QUESTION_MAX_LENGTH, type LandingQuestionOrigin } from '../services/guestQuestion';
import { landingQuestionDraftService, type LandingQuestionDraft } from '../services/landingQuestionDraft';
import { askPublicGuestQuestion, getGuestSignupReason, type GuestAnswerReceipt } from '../services/publicGuestQuestion';
import { isLoginRoute, isRegisterRoute } from '../utils/publicRoutes';
import { isBotProtectionError } from '../utils/botProtectionErrors';
import { useBotProtection } from './useBotProtection';

export type PublicGuestQuestionPhase = 'idle' | 'verifying' | 'waiting' | 'complete' | 'error' | 'signup';

export function usePublicGuestQuestion({ onGetStarted, onSignIn }: { onGetStarted: () => void; onSignIn: () => void }) {
  const { language } = useTranslation();
  const [question, setQuestionState] = useState('');
  const editingDraftRef = useRef<LandingQuestionDraft | null>(null);
  const [website, setWebsite] = useState('');
  const [receipt, setReceipt] = useState<GuestAnswerReceipt | null>(null);
  const [phase, setPhase] = useState<PublicGuestQuestionPhase>('idle');
  const [errorKind, setErrorKind] = useState<'protection' | 'request' | 'save' | null>(null);
  const [submittedQuestion, setSubmittedQuestion] = useState<{ question: string; language: Language } | null>(null);
  const requestActiveRef = useRef(false);
  const mountedRef = useRef(true);
  const leftForAuthenticationRef = useRef(false);
  const preserveForAuthenticationRef = useRef<(() => void) | null>(null);
  const botProtection = useBotProtection('guest_question', {
    size: 'compact', requireSiteKey: true,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      leftForAuthenticationRef.current = isLoginRoute() || isRegisterRoute();
      if (leftForAuthenticationRef.current) preserveForAuthenticationRef.current?.();
    };
  }, []);

  useEffect(() => {
    const restoreDraft = () => {
      const draft = landingQuestionDraftService.read();
      editingDraftRef.current = draft;
      setQuestionState(draft?.question ?? '');
    };
    restoreDraft();
    const unsubscribe = landingQuestionDraftService.subscribe(restoreDraft);
    window.addEventListener('pageshow', restoreDraft);
    return () => {
      unsubscribe();
      window.removeEventListener('pageshow', restoreDraft);
    };
  }, []);

  const updateQuestion = (value: string, origin: LandingQuestionOrigin) => {
    editingDraftRef.current = landingQuestionDraftService.save(value, language, origin);
    setQuestionState(value);
  };
  const setQuestion = (value: string) => updateQuestion(value, 'typed');
  const selectExample = (value: string) => updateQuestion(value, 'example');

  const mutation = useMutation({
    retry: false,
    mutationFn: async (draft: { question: string; language: Language; website: string; prepareRequest: () => string }) => {
      const captchaToken = await botProtection.getCaptchaToken();
      if (!captchaToken) throw new Error('BOT_PROTECTION_UNAVAILABLE');
      const recoveryToken = draft.prepareRequest();
      if (mountedRef.current) setPhase('waiting');
      return askPublicGuestQuestion({
        question: draft.question, locale: draft.language, website: draft.website, captchaToken, recoveryToken,
      });
    },
  });

  const submit = async () => {
    const normalizedQuestion = question.trim();
    if (!normalizedQuestion || normalizedQuestion.length > GUEST_QUESTION_MAX_LENGTH
      || requestActiveRef.current || phase === 'complete' || phase === 'signup') return;
    requestActiveRef.current = true;
    const editingDraftId = editingDraftRef.current?.id ?? null;
    const handoffResetVersion = guestQuestionService.getResetVersion();
    let handoffMarker = guestQuestionService.getHandoffMarker();
    let expectedEditingDraftId = editingDraftId;
    let manualHandoffId: string | null = null;
    const stillCurrent = () => guestQuestionService.getResetVersion() === handoffResetVersion
      && guestQuestionService.getHandoffMarker() === handoffMarker
      && (landingQuestionDraftService.read()?.id ?? null) === expectedEditingDraftId;
    const draft = { question: normalizedQuestion, language: editingDraftRef.current?.language ?? language, website, origin: editingDraftRef.current?.origin ?? 'typed' };
    const preserveManualQuestion = () => {
      if (!stillCurrent()) {
        // Remove only this recovery if the user replaced or cleared its editor.
        if (manualHandoffId && guestQuestionService.getResetVersion() === handoffResetVersion
          && guestQuestionService.getHandoffMarker() === handoffMarker) guestQuestionService.discardPending(manualHandoffId);
        return;
      }
      if (manualHandoffId === guestQuestionService.read()?.id) return;
      if (guestQuestionService.save(draft.question, draft.language, { autoSubmit: false, origin: draft.origin })) {
        manualHandoffId = guestQuestionService.read()?.id ?? null;
        handoffMarker = guestQuestionService.getHandoffMarker();
      }
    };
    // Keep the submitted text before any asynchronous security work. Only an
    // actual outgoing request receives a recovery proof; captcha failures stay editable.
    preserveManualQuestion();
    if (!manualHandoffId) {
      requestActiveRef.current = false;
      setErrorKind('save');
      setPhase('error');
      return;
    }
    preserveForAuthenticationRef.current = preserveManualQuestion;
    setSubmittedQuestion(draft);
    setReceipt(null);
    setErrorKind(null);
    setPhase('verifying');
    try {
      const result = await mutation.mutateAsync({ ...draft, prepareRequest: () => {
        if (!mountedRef.current || !stillCurrent() || !manualHandoffId) throw new Error('GUEST_QUESTION_CANCELLED');
        const bytes = crypto.getRandomValues(new Uint8Array(32));
        const token = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        if (!guestQuestionService.attachRecovery(manualHandoffId, token)) throw new Error('GUEST_QUESTION_RECOVERY_UNAVAILABLE');
        handoffMarker = guestQuestionService.getHandoffMarker();
        return token;
      } });
      if (!mountedRef.current && leftForAuthenticationRef.current) {
        // The authenticated workspace recovers this operation using its proof;
        // a late public receipt never authorizes a second generation.
        preserveManualQuestion();
        return;
      }
      if (guestQuestionService.getResetVersion() !== handoffResetVersion) return;
      const ownsCurrentQuestion = stillCurrent();
      landingQuestionDraftService.clearIfCurrent(editingDraftId);
      if (ownsCurrentQuestion) expectedEditingDraftId = null;
      if (!mountedRef.current) return;
      setReceipt(result);
      setPhase('complete');
    } catch (error) {
      const reason = getGuestSignupReason(error);
      if (reason) {
        if (!stillCurrent()) { preserveManualQuestion(); return; }
        if (!guestQuestionService.save(draft.question, draft.language, { origin: draft.origin })) {
          if (!mountedRef.current) return;
          setErrorKind('save');
          setPhase('error');
          return;
        }
        // Navigation to authentication may finish before the refusal arrives.
        // Preserve the submitted question even if the landing has unmounted.
        if (!mountedRef.current) return;
        setPhase('signup');
        if (reason === 'account-login') onSignIn();
        else onGetStarted();
        return;
      }
      if (!mountedRef.current) {
        if (leftForAuthenticationRef.current) preserveManualQuestion();
        return;
      }
      setErrorKind(isBotProtectionError(error) ? 'protection' : 'request');
      setPhase('error');
    } finally {
      requestActiveRef.current = false;
      botProtection.resetCaptcha();
    }
  };

  const isBusy = phase === 'verifying' || phase === 'waiting' || phase === 'signup';
  return {
    question, setQuestion, selectExample, website, setWebsite, receipt, phase, errorKind,
    submittedQuestion, botProtection, isBusy, submit,
  };
}
